class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.closingTimeout;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.pointerEnterHandler = this.onPointerEnter.bind(this);
    this.pointerLeaveHandler = this.onPointerLeave.bind(this);

    if(!this.dataset.ignoreHover) {
      this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;

      this.debouncedOnChange = debounce((event) => {
        this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;
        this.initHoverDetails();
      }, 300);

      window.addEventListener('resize', this.debouncedOnChange.bind(this));

      this.initHoverDetails();
    }

    this.initClickSummary();
  }

  initHoverDetails () {
    if(this.isAbleToUseMouse) {
      Shopify.addListener(this.mainDetailsToggle, 'pointerenter', this.pointerEnterHandler);
      Shopify.addListener(this.mainDetailsToggle, 'pointerleave', this.pointerLeaveHandler);
    } else {
      this.mainDetailsToggle.removeEventListener('pointerenter', this.pointerEnterHandler);
      this.mainDetailsToggle.removeEventListener('pointerleave', this.pointerLeaveHandler);
    }
  }

  // Touch taps on hover-capable screens fire enter events too; the click handler opens and closes for them.
  onPointerEnter(event) {
    if (event.pointerType === 'touch') return;
    this.open();
  }

  onPointerLeave(event) {
    if (event.pointerType === 'touch') return;
    this.closeWithClosingClass();
  }

  initClickSummary() {
    this.mainDetailsToggle.querySelector('summary').addEventListener('click', (e) => {
      e.preventDefault();
      if (this.mainDetailsToggle.hasAttribute('open')) {
        this.closeWithClosingClass();
      } else {
        this.open();
      }
    });
  }

  closeWithClosingClass() {
    if(this.classList.contains('ignore-close')) return;
    this.mainDetailsToggle.classList.add('closing');
    if(this.closingTimeout) {
      clearTimeout(this.closingTimeout);
    }
    this.closingTimeout = setTimeout(() => {
      this.close();
    }, 300);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.closeWithClosingClass();
    this.mainDetailsToggle.querySelector('summary').focus();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeWithClosingClass();
    })
  }

  // Coming back while the panel fades out cancels the close, so it can't shut under the pointer.
  // An open panel is left as it is rather than replaying its opening animation.
  open() {
    clearTimeout(this.closingTimeout);
    this.mainDetailsToggle.classList.remove('closing');
    if (this.mainDetailsToggle.hasAttribute('open')) return;
    this.mainDetailsToggle.setAttribute('open', true);
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
  }

  close() {
    clearTimeout(this.closingTimeout);
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.classList.remove('closing');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

let hoverSupport = matchMedia('(hover: hover)').matches;
window.addEventListener('resize', () => {
  hoverSupport = matchMedia('(hover: hover)').matches;
});

class HeaderMenu extends DetailsDisclosure {
  // Hover intent, in ms: how long the pointer rests on an item before its panel opens,
  // how long it rests before swapping from another open panel, and how long a panel
  // waits after the pointer leaves before it starts to fade out.
  static OPEN_DELAY = 120;
  static SWITCH_DELAY = 90;
  static CLOSE_DELAY = 200;

  constructor() {
    super();
    this.header = document.querySelector('.header');
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    this.querySelectorAll(':scope > details > .header__menu-item--link').forEach(element => element.addEventListener('click', this.onClickSummary));
  }

  onClickSummary(event) {
    if(hoverSupport) {
      event.preventDefault();
      window.location = event.currentTarget.dataset.href;
    }
  }

  // A pointer that only crosses the menu bar on its way somewhere else opens nothing, and
  // one that slides between items swaps the panel in place without a fade in between.
  onPointerEnter(event) {
    if (event.pointerType === 'touch') return;
    clearTimeout(this.hoverTimeout);
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.open();
      return;
    }
    const delay = this.hasOtherOpenMenu() ? HeaderMenu.SWITCH_DELAY : HeaderMenu.OPEN_DELAY;
    this.hoverTimeout = setTimeout(() => {
      const swapping = this.closeOtherMenus();
      this.open();
      if (swapping) this.content.getAnimations().forEach(animation => animation.finish());
    }, delay);
  }

  onPointerLeave(event) {
    if (event.pointerType === 'touch') return;
    clearTimeout(this.hoverTimeout);
    if (!this.mainDetailsToggle.hasAttribute('open')) return;
    this.hoverTimeout = setTimeout(() => this.closeWithClosingClass(), HeaderMenu.CLOSE_DELAY);
  }

  otherMenus() {
    return Array.from(document.querySelectorAll('header-menu')).filter(menu => menu !== this && menu.mainDetailsToggle.hasAttribute('open') && !menu.classList.contains('ignore-close'));
  }

  hasOtherOpenMenu() {
    return this.otherMenus().length > 0;
  }

  // Only one panel at a time: two stacked panels mid-fade are what made the menu flicker.
  closeOtherMenus() {
    const menus = this.otherMenus();
    menus.forEach(menu => menu.close());
    return menus.length > 0;
  }

  close() {
    clearTimeout(this.hoverTimeout);
    super.close();
  }

  // The page is not scroll-locked while a panel is open: toggling the page scrollbar shifted
  // the header sideways, and one panel closing unlocked the page under the next. A panel too
  // tall for the screen scrolls on its own instead (component-mega-menu.css).
  onToggle() {
    if (!this.header) return;
    if(this.mainDetailsToggle.hasAttribute('open')) {
      // Measured on every open, since the sticky header sits higher once the page has scrolled.
      const headerBottom = Math.floor(this.header.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--header-bottom-position-desktop', `${headerBottom}px`);
      // scrollHeight ignores the opening animation's offset, which getBoundingClientRect would include.
      this.content.classList.toggle('outside-viewport', headerBottom + this.content.scrollHeight > document.documentElement.clientHeight);
    } else {
      this.content.classList.remove('outside-viewport');
    }
  }
}

customElements.define('header-menu', HeaderMenu);
